import React, { useContext } from 'react';
import { AgFinancingPhase3Status, ContractStatus, Phase3 } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import {
  CreditExceptionParams,
  DecisionOptions,
  DocumentsValidationAvailableDecisions,
  DocumentsValidationDecision
} from '../../DocumentsValidationDecisions/DocumentsValidationDecisions';
import { UserContext } from 'core/components/GateWay/GateWay';
import { Phase3Context } from 'features/brazil-grower/components/BrazilGrowerFinance/phases/Phase3';
import {
  getCreditApprovalData,
  updateCreditApprovalRoundExceptions
} from 'features/brazil-grower/queries/creditApproval.queries';

import { useMutation } from '@apollo/client';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';

interface DocumentsValidationCreditAnalystAndManagerDecisionsProps {
  contractStatus: Phase3['contractStatus'];
  hasDocuments: boolean;
}

type reviewNotesFields = 'signedDocumentsReviewNotes' | 'amendmentCreationNotes';

export const DocumentsValidationCreditAnalystAndManagerDecisions: React.FunctionComponent<DocumentsValidationCreditAnalystAndManagerDecisionsProps> = ({
  contractStatus,
  hasDocuments
}) => {
  /**
   * User Context
   */
  const { isCreditLead } = useContext(UserContext);

  /**
   * Collateral Context
   */
  const { updatePhase } = useContext(Phase3Context);

  const { agFinancingId } = useContext(FinanceProcessContext);

  const [updateCreditApprovalRoundExceptionsFn] = useMutation(updateCreditApprovalRoundExceptions);

  const updateExceptions = async ({ roundId, creditExceptions }: CreditExceptionParams) => {
    return updateCreditApprovalRoundExceptionsFn({
      variables: {
        creditApprovalRoundId: roundId,
        creditApprovalRoundExceptionsInput: {
          selectedExceptions: creditExceptions
        }
      },
      refetchQueries: [
        {
          query: getCreditApprovalData,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    });
  };

  const confirmContractStatusDecision = (
    contractStatus: ContractStatus,
    notesField: reviewNotesFields = 'signedDocumentsReviewNotes'
  ) => {
    return async (reviewNotes: string, creditExceptionParams?: CreditExceptionParams) => {
      if (creditExceptionParams && contractStatus === ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER) {
        await updateExceptions(creditExceptionParams);
      }

      return updatePhase({
        contractStatus,
        [notesField]: reviewNotes
      });
    };
  };

  const onHoldPhase3 = (reviewNotes: string) => {
    return updatePhase({
      status: AgFinancingPhase3Status.ON_HOLD,
      signedDocumentsReviewNotes: reviewNotes
    });
  };

  const getAvailableDecisions = () => {
    const availableDecisions: DocumentsValidationAvailableDecisions = {};

    if (hasDocuments) {
      availableDecisions['APPROVE'] = {
        descriptionMessage: i18next.t('signedDocumentsApprovedByCAOrCMMessage'),
        successMessage: i18next.t('signedDocumentsApprovedByCAOrCMSuccessMessage'),
        onConfirmAction: confirmContractStatusDecision(ContractStatus.REVIEWING_SIGNED_DOCUMENTS)
      };

      availableDecisions['DENY'] = {
        descriptionMessage: i18next.t('signedDocumentsDeniedByCAOrCMMessage'),
        successMessage: i18next.t('signedDocumentsDeniedByCAOrCMSuccessMessage'),
        onConfirmAction: confirmContractStatusDecision(ContractStatus.SIGNED_DOCUMENTS_DENIED)
      };

      if (!isCreditLead && contractStatus !== ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER) {
        availableDecisions['SEND_TO_COMMITTEE'] = {
          descriptionMessage: i18next.t('signedDocumentsSendToCommitteeMessage'),
          successMessage: i18next.t('signedDocumentsSentToCreditManager'),
          onConfirmAction: confirmContractStatusDecision(ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER)
        };
      }
    }

    availableDecisions[DecisionOptions.APPROVE_AMENDMENT_CREATION] = {
      descriptionMessage: i18next.t('approveAmendmentCreationMessage'),
      successMessage: i18next.t('amendmentCreationApproved'),
      onConfirmAction: confirmContractStatusDecision(ContractStatus.CREATING_AMENDMENT, 'amendmentCreationNotes')
    };

    availableDecisions['PUT_ON_HOLD'] = {
      descriptionMessage: i18next.t('phase3OnHoldDisclaimer'),
      successMessage: i18next.t('phase3OnHoldSuccessMessage'),
      onConfirmAction: onHoldPhase3
    };

    return availableDecisions;
  };

  return <DocumentsValidationDecision availableDecisions={getAvailableDecisions()} />;
};
